import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ArrowLeft, Check, X, User, Clock, Gavel, MessageCircle, Building2, Phone, Send, Trash2, Plus, Hand, Edit } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { getPhotoUrl } from '@/utils/photoUrl';
import { useAuth } from '@/contexts/AuthContext';
import { isActiveCliente, isReenviarCandidate } from '@/utils/clientes';
import NewManualOfferDialog from '@/components/ofertas/NewManualOfferDialog';
import { ImageGallery } from '@/components/ui/image-gallery';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

import TipoGanadoCategoriaSelector from '@/components/TipoGanadoCategoriaSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const badgeVariants = {
  Borrador: 'secondary',
  Enviada: 'default',
  Adjudicada: 'outline',
  Pendiente: 'default',
  Aceptada: 'outline',
  Rechazada: 'destructive',
  'En espera': 'secondary',
  'No adjudicada': 'outline',
};

const DetallePublicacion = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lote, setLote] = useState(null);
  const [ofertas, setOfertas] = useState([]);
  const [clientes, setClientes] = useState({});
  const [sucursales, setSucursales] = useState({});
  const [loading, setLoading] = useState(true);
  const [ofertaToAccept, setOfertaToAccept] = useState(null);
  const [ofertaToReject, setOfertaToReject] = useState(null);
  const [selectedCliente, setSelectedCliente] = useState(null);

  const [showReenviarDialog, setShowReenviarDialog] = useState(false);
  const [selectedClientesReenviar, setSelectedClientesReenviar] = useState([]);
  const [reenviando, setReenviando] = useState(false);

  const [mostrarBorradorDialog, setMostrarBorradorDialog] = useState(false);
  const [comentarioNoAdjudicada, setComentarioNoAdjudicada] = useState('');
  const [showNoAdjudicadoDialog, setShowNoAdjudicadoDialog] = useState(false);
  const [showEsperaDialog, setShowEsperaDialog] = useState(false);
  const [motivoEspera, setMotivoEspera] = useState('');
  const [fechaEspera, setFechaEspera] = useState('');

  const [showNewOfferDialog, setShowNewOfferDialog] = useState(false);
  const [processingOffer, setProcessingOffer] = useState(null); // Track which offer is being processed

  const [showEditTipoGanadoCategoria, setShowEditTipoGanadoCategoria] = useState(false);
  const [editTipoGanado, setEditTipoGanado] = useState('');
  const [editCategoria, setEditCategoria] = useState('');
  const [savingTipoGanadoCategoria, setSavingTipoGanadoCategoria] = useState(false);
  
  const [showAdjudicarDialog, setShowAdjudicarDialog] = useState(false);

  const [showExtenderDialog, setShowExtenderDialog] = useState(false);
  const [horasExtender, setHorasExtender] = useState(1);
  const [extendiendo, setExtendiendo] = useState(false);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [loteRes, ofertasRes, clientesRes, sucursalesRes] = await Promise.all([
        axios.get(`${API}/lotes/${id}`),
        axios.get(`${API}/ofertas/lote/${id}`),
        axios.get(`${API}/clientes?includeDeleted=true`),
        axios.get(`${API}/sucursales`),
      ]);

      setLote(loteRes.data);
      setOfertas(ofertasRes.data);

      const revMap = {};
      clientesRes.data.forEach((r) => {
        revMap[r.id] = r;
      });
      setClientes(revMap);

      const sucMap = {};
      sucursalesRes.data.forEach((s) => {
        sucMap[s.id] = s;
      });
      setSucursales(sucMap);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar detalle');
    } finally {
      setLoading(false);
    }
  };

  // handleAcceptOffer removed as logic moved to handlePasarAEspera

  const handleRejectOffer = async (ofertaId) => {
    if (processingOffer) return;
    setProcessingOffer(ofertaId);
    try {
      await axios.put(`${API}/ofertas/${ofertaId}`, { estado: 'Rechazada' });
      toast.success('Oferta rechazada');
      loadData();
    } catch (error) {
      console.error('Error rejecting offer:', error);
      toast.error('Error al rechazar oferta');
    } finally {
      setProcessingOffer(null);
    }
    setOfertaToReject(null);
  };

  const handleSuperAdminStatusChange = async (newStatus) => {
    if (newStatus === 'En espera') {
      const defaultDate = new Date();
      defaultDate.setHours(defaultDate.getHours() + 24);
      const tzoffset = defaultDate.getTimezoneOffset() * 60000;
      const localISOTime = new Date(defaultDate - tzoffset).toISOString().slice(0, 16);
      setFechaEspera(localISOTime);
      setMotivoEspera('');
      setShowEsperaDialog(true);
      return;
    }
    if (newStatus === 'No adjudicada') {
      setComentarioNoAdjudicada('');
      setShowNoAdjudicadoDialog(true);
      return;
    }

    try {
      await axios.put(`${API}/lotes/${id}`, { estadoPublicacion: newStatus });
      toast.success('Estado del vehículo actualizado');
      loadData();
    } catch (error) {
      console.error('Error updating vehicle status:', error);
      toast.error('Error al actualizar estado del vehículo');
    }
  };

  const handleSuperAdminOfferStatusChange = async (ofertaId, newStatus) => {
    try {
      await axios.put(`${API}/ofertas/${ofertaId}`, { estado: newStatus });
      toast.success('Estado de la oferta actualizado');
      loadData();
    } catch (error) {
      console.error('Error updating offer status:', error);
      toast.error('Error al actualizar estado de la oferta');
    }
  };

  const handleDeleteOffer = async (ofertaId) => {
    if (!window.confirm("¿Está seguro de que desea eliminar permanentemente esta oferta?")) return;
    try {
      await axios.delete(`${API}/ofertas/${ofertaId}`);
      toast.success('Oferta eliminada');
      loadData();
    } catch (error) {
      console.error('Error deleting offer:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar oferta');
    }
  };

  const handlePasarAEspera = async () => {
    if (!motivoEspera || !fechaEspera) {
      toast.error('La fecha y el motivo son obligatorios');
      return;
    }
    try {
      const isoDate = new Date(fechaEspera).toISOString();
      if (ofertaToAccept) {
        setProcessingOffer(ofertaToAccept);
        await axios.put(`${API}/ofertas/${ofertaToAccept}`, { 
          estado: 'Aceptada',
          fechaEspera: isoDate,
          motivoEspera: motivoEspera
        });
        toast.success('Oferta aceptada y publicación en espera');
      } else {
        await axios.put(`${API}/lotes/${id}`, {
          estadoPublicacion: 'En espera',
          fechaEspera: isoDate,
          motivoEspera: motivoEspera
        });
        toast.success('Publicación en espera actualizada');
      }
      setShowEsperaDialog(false);
      setOfertaToAccept(null);
      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast.error(ofertaToAccept ? 'Error al aceptar la oferta' : 'Error al actualizar publicación a En espera');
    } finally {
      if (ofertaToAccept) {
        setProcessingOffer(null);
      }
    }
  };

  const handleNoAdjudicado = async () => {
    if (ofertas.length > 0 && !comentarioNoAdjudicada.trim()) {
      toast.error('El comentario es obligatorio cuando hay ofertas');
      return;
    }
    try {
      await axios.put(`${API}/lotes/${id}`, {
        estadoPublicacion: 'No adjudicada',
        comentarioNoAdjudicada: comentarioNoAdjudicada.trim() || undefined
      });
      toast.success('Publicación tipoGanadoda como No adjudicada');
      setShowNoAdjudicadoDialog(false);
      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al actualizar publicación a No adjudicada');
    }
  };

  const handleAdjudicar = async () => {
    try {
      await axios.put(`${API}/lotes/${id}`, { estadoPublicacion: 'Adjudicada' });
      toast.success('Publicación adjudicada exitosamente');
      setShowAdjudicarDialog(false);
      loadData();
    } catch (error) {
      console.error('Error adjudicando:', error);
      toast.error(error.response?.data?.detail || 'Error al adjudicar la publicación');
    }
  };

  const handleSelectBidders = () => {
    const bidderIds = [...new Set(ofertas.map((o) => o.clienteId))];
    const activeBidderIds = bidderIds.filter(id => {
      return isReenviarCandidate(clientes[id]);
    });
    setSelectedClientesReenviar(activeBidderIds);
  };

  const handleReenviar = async () => {
    if (selectedClientesReenviar.length === 0) {
      toast.error('Selecciona al menos un cliente');
      return;
    }

    setReenviando(true);
    try {
      const res = await axios.post(`${API}/lotes/${id}/reenviar`, {
        clienteIds: selectedClientesReenviar
      });
      toast.success(`Publicación reenviada a ${res.data.enviados} clientes`);
      setShowReenviarDialog(false);
      setSelectedClientesReenviar([]);
    } catch (error) {
      console.error('Error reenviando:', error);
      toast.error('Error al reenviar la publicación');
    } finally {
      setReenviando(false);
    }
  };

  const handleExtender = async () => {
    if (!horasExtender || horasExtender < 1) {
      toast.error('La cantidad de horas debe ser al menos 1');
      return;
    }

    setExtendiendo(true);
    try {
      await axios.post(`${API}/lotes/${id}/extender`, { horas: parseInt(horasExtender, 10) });
      toast.success(`Plazo extendido por ${horasExtender} horas exitosamente.`);
      setShowExtenderDialog(false);
      setHorasExtender(1);
      loadData();
    } catch (error) {
      console.error('Error extendiendo plazo:', error);
      toast.error(error.response?.data?.detail || 'Error al extender el plazo');
    } finally {
      setExtendiendo(false);
    }
  };

  const toggleClienteSelection = (clienteId) => {
    setSelectedClientesReenviar(prev =>
      prev.includes(clienteId)
        ? prev.filter(rId => rId !== clienteId)
        : [...prev, clienteId]
    );
  };

  const formatWhatsappLink = (phone) => {
    if (!phone) return null;
    const cleaned = phone.replace(/[^0-9+]/g, '');
    return `https://wa.me/${cleaned.replace('+', '')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">Cargando...</div>
      </div>
    );
  }

  if (!lote) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <div className="text-muted-foreground">Vehículo no encontrado</div>
        <Button onClick={() => navigate('/publicaciones')}>Volver</Button>
      </div>
    );
  }

  const sucursal = lote.sucursalId ? sucursales[lote.sucursalId] : null;
  const isVencida = lote && lote.fechaLimiteOfertas && new Date(lote.fechaLimiteOfertas) < new Date() && !['Adjudicada', 'Borrador', 'En espera', 'No adjudicada'].includes(lote.estadoPublicacion);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-background border-b">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <Button
            variant="ghost"
            className="mb-4"
            onClick={() => navigate('/publicaciones')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver a Publicaciones
          </Button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-semibold tracking-tight">
                  {lote.tipoGanado} {lote.categoria}
                </h1>
                {lote.esSubasta && (
                  <Badge variant="secondary" className="gap-1">
                    <Gavel className="h-3 w-3" />
                    Subasta
                  </Badge>
                )}
              </div>
              <p className="text-muted-foreground mt-1 text-sm font-medium">
                {(lote.razas && lote.razas.length > 0) ? `${lote.razas.map(r => r.nombre).join(', ')} • ` : (lote.raza ? `${lote.raza} • ` : '')}{lote.numeroLote} • {lote.cantidad?.toLocaleString()} cantidad
                <span className="ml-2 text-xs opacity-60 font-normal">({lote.id})</span>
              </p>

              {lote.interaccionesWhatsapp > 0 && (
                <div className="mt-3 flex items-center">
                  <Badge variant="secondary" className="gap-1.5 bg-green-50 text-green-700 hover:bg-green-100 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800">
                    <MessageCircle className="h-3.5 w-3.5" />
                    {lote.interaccionesWhatsapp} {lote.interaccionesWhatsapp === 1 ? 'Cliente Interactuó' : 'Clientes Interactuaron'}
                  </Badge>
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {(lote.estadoPublicacion === 'Enviada' || lote.estadoPublicacion === 'En espera') && (
                <Button
                  onClick={() => setShowNewOfferDialog(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Cargar Oferta Manual
                </Button>
              )}
              {lote.estadoPublicacion === 'Enviada' && !isVencida && (
                <Button
                  variant="outline"
                  onClick={() => setShowReenviarDialog(true)}
                >
                  <Send className="h-4 w-4 mr-2" />
                  Reenviar
                </Button>
              )}
              {lote.estadoPublicacion === 'Enviada' && (
                <Button
                  variant="outline"
                  onClick={() => setShowExtenderDialog(true)}
                >
                  <Clock className="h-4 w-4 mr-2" />
                  Extender Plazo
                </Button>
              )}
              {lote.estadoPublicacion === 'En espera' && (
                <Button 
                  onClick={() => setShowAdjudicarDialog(true)} 
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Check className="h-4 w-4 mr-2" />
                  Adjudicar
                </Button>
              )}
              {(isVencida || lote.estadoPublicacion === 'En espera') && (
                <Button variant="outline" className="text-muted-foreground" onClick={() => {
                  setComentarioNoAdjudicada('');
                  setShowNoAdjudicadoDialog(true);
                }}>
                  No adjudicada
                </Button>
              )}
              {user?.rol === 'SuperAdmin' ? (
                <Select value={lote.estadoPublicacion} onValueChange={handleSuperAdminStatusChange}>
                  <SelectTrigger className={`h-8 w-[140px] text-xs font-medium border-0 focus:ring-0 ${lote.estadoPublicacion === 'Borrador' ? 'bg-secondary text-secondary-foreground' : lote.estadoPublicacion === 'Rechazada' ? 'bg-destructive text-destructive-foreground' : lote.estadoPublicacion === 'Enviada' ? 'bg-primary text-primary-foreground' : lote.estadoPublicacion === 'Adjudicada' ? 'border bg-background' : 'bg-secondary text-secondary-foreground'}`}>
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Borrador">Borrador</SelectItem>
                    <SelectItem value="Enviada">Enviada</SelectItem>
                    <SelectItem value="Adjudicada">Adjudicada</SelectItem>
                    <SelectItem value="En espera">En espera</SelectItem>
                    <SelectItem value="No adjudicada">No adjudicada</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Badge variant={badgeVariants[lote.estadoPublicacion]} data-testid="listing-status-badge">
                  {lote.estadoPublicacion}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
          {/* Left Column: Photos and Details */}
          <div className="space-y-6">
            {/* Image Carousel */}
            <Card data-testid="image-carousel">
              <CardContent className="p-6">
                
                {lote.enlaceVideo ? (
                  lote.enlaceVideo.includes('youtube.com') || lote.enlaceVideo.includes('youtu.be') ? (
                    <div className="aspect-video w-full overflow-hidden rounded-md">
                      <iframe 
                        src={lote.enlaceVideo.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')} 
                        className="w-full h-full" 
                        allowFullScreen 
                      />
                    </div>
                  ) : (
                    <div className="aspect-video w-full overflow-hidden rounded-md bg-black">
                      <video 
                        src={lote.enlaceVideo} 
                        controls 
                        className="w-full h-full object-contain"
                      />
                    </div>
                  )
                ) : (
                  <div className="aspect-video w-full flex items-center justify-center bg-muted rounded-md text-muted-foreground">
                    Sin video disponible
                  </div>
                )}
                
                {lote.latitud && lote.longitud && (
                  <div className="mt-6">
                    <h3 className="font-semibold mb-2">Ubicación</h3>
                    <div className="h-[200px] w-full rounded-md border overflow-hidden relative z-0">
                      <MapContainer center={[lote.latitud, lote.longitud]} zoom={8} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
                        <TileLayer
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <Marker position={[lote.latitud, lote.longitud]} />
                      </MapContainer>
                    </div>
                  </div>
                )}

              </CardContent>
            </Card>

            {/* Vehicle Details */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Detalles del Vehículo</CardTitle>
                  {(user?.rol === 'Admin' || user?.rol === 'SuperAdmin') && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditTipoGanado(lote.tipoGanado || '');
                        setEditCategoria(lote.categoria || '');
                        setShowEditTipoGanadoCategoria(true);
                      }}
                      className="gap-1.5 text-xs"
                      data-testid="edit-tipoGanado-categoria-button"
                    >
                      <Edit className="h-3.5 w-3.5" />
                      Editar TipoGanado/Categoria
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">TipoGanado:</span>{' '}
                    <span className="font-medium">{lote.tipoGanado}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Categoria:</span>{' '}
                    <span className="font-medium">{lote.categoria}</span>
                  </div>
                  {(lote.razas && lote.razas.length > 0) ? (
                    <div className="col-span-full border-t pt-3 mt-1">
                      <span className="text-muted-foreground block mb-2">Composición de Razas:</span>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {lote.razas.map((r, i) => (
                          <div key={i} className="bg-accent/50 p-2 rounded-md text-center">
                            <span className="font-semibold block">{r.cantidad}</span>
                            <span className="text-xs text-muted-foreground uppercase tracking-wider">{r.nombre}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : lote.raza && (
                    <div>
                      <span className="text-muted-foreground">Raza:</span>{' '}
                      <span className="font-medium">{lote.raza}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground">Lote:</span>{' '}
                    <span className="font-medium">{lote.numeroLote || '-'}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Cantidad (Cabezas):</span>{' '}
                    <span className="font-medium">{lote.cantidad?.toLocaleString()}</span>
                  </div>
                  {lote.pesoPromedio && (
                    <div>
                      <span className="text-muted-foreground">Peso Promedio:</span>{' '}
                      <span className="font-medium">{lote.pesoPromedio} kg</span>
                    </div>
                  )}
                  {lote.departamento && (
                    <div>
                      <span className="text-muted-foreground">Departamento:</span>{' '}
                      <span className="font-medium">{lote.departamento}</span>
                    </div>
                  )}
                  {lote.establecimiento && (
                    <div>
                      <span className="text-muted-foreground">Establecimiento:</span>{' '}
                      <span className="font-medium">{lote.establecimiento}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground">Modalidad:</span>{' '}
                    <span className="font-medium">{lote.esSubasta ? 'Subasta' : 'Oferta directa'}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Autor:</span>{' '}
                    <span className="font-medium">{lote.autor}</span>
                  </div>
                  {lote.createdAt && (
                    <div>
                      <span className="text-muted-foreground">Creada:</span>{' '}
                      <span className="font-medium">
                        {format(new Date(lote.createdAt), "dd/MM/yyyy HH:mm", { locale: es })}
                      </span>
                    </div>
                  )}
                </div>

                {/* Fecha límite de ofertas */}
                {lote.fechaLimiteOfertas && (
                  <div className="mt-4 p-3 rounded-md border bg-accent/30 flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <div>
                      <span className="text-muted-foreground">Fecha límite de ofertas:</span>{' '}
                      <span className="font-medium">
                        {format(new Date(lote.fechaLimiteOfertas), "dd/MM/yyyy HH:mm", { locale: es })}
                      </span>
                      {new Date(lote.fechaLimiteOfertas) < new Date() && (
                        <Badge variant="destructive" className="ml-2 text-xs">Vencida</Badge>
                      )}
                    </div>
                  </div>
                )}

                {/* Datos del vendedor interno */}
                {(lote.vendedorNombre || lote.vendedorTelefono) && (
                  <div className="sm:col-span-2 mt-4 p-3 bg-accent/50 rounded-md border text-xs">
                    <div className="font-semibold mb-1 text-muted-foreground">Datos del Cliente (Uso interno):</div>
                    <div>
                      {lote.vendedorNombre && <span className="font-medium mr-3">Nombre: {lote.vendedorNombre}</span>}
                      {lote.vendedorTelefono && <span className="font-medium">Teléfono: {lote.vendedorTelefono}</span>}
                    </div>
                  </div>
                )}

                {(lote.observaciones || lote.comentarios) && (
                  <div className="mt-4">
                    <span className="text-sm text-muted-foreground">Observaciones:</span>
                    <p className="text-sm mt-1">{lote.observaciones || lote.comentarios}</p>
                  </div>
                )}
                {(lote.sanidad || lote.trazabilidad) && (
                  <div className="mt-4 grid sm:grid-cols-2 gap-4 text-sm">
                    {lote.sanidad && <div><span className="text-muted-foreground">Sanidad:</span> <span className="font-medium">{lote.sanidad}</span></div>}
                    {lote.trazabilidad && <div><span className="text-muted-foreground">Trazabilidad:</span> <span className="font-medium">{lote.trazabilidad}</span></div>}
                  </div>
                )}

                {lote.estadoPublicacion === 'En espera' && (
                  <div className="mt-4 p-3 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-900/20">
                    <div className="flex items-center justify-between gap-2 text-amber-800 dark:text-amber-400 font-semibold mb-2">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        En Espera
                      </div>
                      {(user?.rol === 'Admin' || user?.rol === 'SuperAdmin') && (
                        <Button variant="ghost" size="sm" className="h-6 text-xs px-2" onClick={() => {
                          const dt = lote.fechaEspera ? new Date(lote.fechaEspera) : new Date();
                          const tzoffset = dt.getTimezoneOffset() * 60000;
                          setFechaEspera(new Date(dt - tzoffset).toISOString().slice(0, 16));
                          setMotivoEspera(lote.motivoEspera || '');
                          setShowEsperaDialog(true);
                        }}>
                          <Edit className="h-3 w-3 mr-1" /> Editar
                        </Button>
                      )}
                    </div>
                    {lote.fechaEspera && (
                      <div className="text-sm text-amber-700 dark:text-amber-500 mb-1">
                        <span className="font-medium">Hasta:</span> {format(new Date(lote.fechaEspera), "dd/MM/yyyy HH:mm", { locale: es })}
                      </div>
                    )}
                    {lote.motivoEspera && (
                      <div className="text-sm text-amber-700 dark:text-amber-500">
                        <span className="font-medium">Motivo:</span> {lote.motivoEspera}
                      </div>
                    )}
                  </div>
                )}

                {lote.estadoPublicacion === 'No adjudicada' && lote.comentarioNoAdjudicada && (
                  <div className="mt-4 p-3 rounded-md border border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-900/10">
                    <div className="flex items-center gap-2 text-red-800 dark:text-red-400 font-semibold mb-2">
                      No Adjudicada
                    </div>
                    <div className="text-sm text-red-700 dark:text-red-300">
                      <span className="font-medium">Comentario:</span> {lote.comentarioNoAdjudicada}
                    </div>
                  </div>
                )}

                <div className="mt-6 pt-6 border-t">
                  <div className="text-2xl font-bold text-primary">
                    {lote.precioBase ? `$${lote.precioBase.toLocaleString('en-US')}` : 'Sin Base'}
                  </div>
                  {lote.precioAdjudicado && (
                    <div className="text-sm text-muted-foreground mt-1">
                      Adjudicado por: ${lote.precioAdjudicado.toLocaleString('en-US')}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column: Offers */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Ofertas Recibidas</CardTitle>
              </CardHeader>
              <CardContent data-testid="offers-list">
                {ofertas.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No hay ofertas aún
                  </p>
                ) : (
                  <div className="space-y-3">
                    {ofertas.map((oferta) => {
                      const cliente = clientes[oferta.clienteId];
                      return (
                        <Card key={oferta.id} className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <button
                                className="font-medium text-left hover:text-primary transition-colors cursor-pointer underline-offset-2 hover:underline"
                                onClick={() => cliente && setSelectedCliente(cliente)}
                              >
                                {cliente?.nombre || 'Cliente'}
                                {cliente?.deletedAt && (
                                  <span className="ml-2 text-xs text-muted-foreground" title={`Eliminado el ${new Date(cliente.deletedAt).toLocaleDateString('es-UY')}`}>
                                    (eliminado)
                                  </span>
                                )}
                              </button>
                              {cliente?.empresa && (
                                <div className="text-xs text-muted-foreground">
                                  {cliente.empresa}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {user?.rol === 'SuperAdmin' && (
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-6 w-6 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteOffer(oferta.id);
                                  }}
                                  title="Eliminar oferta (SuperAdmin)"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                              {user?.rol === 'SuperAdmin' ? (
                                <Select value={oferta.estado} onValueChange={(val) => handleSuperAdminOfferStatusChange(oferta.id, val)}>
                                  <SelectTrigger className={`h-6 text-[10px] w-[100px] border-0 focus:ring-0 ${oferta.estado === 'Aceptada' ? 'border bg-background' : oferta.estado === 'Rechazada' ? 'bg-destructive text-destructive-foreground' : 'bg-primary text-primary-foreground'}`}>
                                    <SelectValue placeholder="Estado" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Pendiente">Pendiente</SelectItem>
                                    <SelectItem value="Aceptada">Aceptada</SelectItem>
                                    <SelectItem value="Rechazada">Rechazada</SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Badge variant={badgeVariants[oferta.estado]}>
                                  {oferta.estado}
                                </Badge>
                              )}
                            </div>
                          </div>

                          <div className="text-xl font-bold text-primary mb-1 flex items-center gap-2">
                            ${oferta.monto.toLocaleString('en-US')}
                            {oferta.esManual && (
                              <Badge variant="outline" className="gap-1 text-[11px] h-5 border-primary text-primary">
                                <Hand className="h-3 w-3" />
                                Manual
                              </Badge>
                            )}
                          </div>

                          <div className="text-xs text-muted-foreground mb-3">
                            {format(new Date(oferta.hora), 'dd/MM/yyyy HH:mm')}
                          </div>

                          {oferta.estado === 'Pendiente' && lote.estadoPublicacion !== 'Adjudicada' && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="default"
                                className="flex-1"
                                onClick={() => {
                                  setOfertaToAccept(oferta.id);
                                  const defaultDate = new Date();
                                  defaultDate.setHours(defaultDate.getHours() + 48); // default to 48h
                                  const tzoffset = defaultDate.getTimezoneOffset() * 60000;
                                  const localISOTime = new Date(defaultDate - tzoffset).toISOString().slice(0, 16);
                                  setFechaEspera(localISOTime);
                                  setMotivoEspera('');
                                  setShowEsperaDialog(true);
                                }}
                                disabled={!!processingOffer}
                                data-testid="accept-offer-button"
                              >
                                {processingOffer === oferta.id ? (
                                  <><div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-1" />Procesando...</>
                                ) : (
                                  <><Check className="h-4 w-4 mr-1" />Aceptar</>
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="flex-1"
                                onClick={() => setOfertaToReject(oferta.id)}
                                disabled={!!processingOffer}
                                data-testid="reject-offer-button"
                              >
                                <X className="h-4 w-4 mr-1" />
                                Rechazar
                              </Button>
                            </div>
                          )}

                          {cliente && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="w-full mt-2"
                              onClick={() => setSelectedCliente(cliente)}
                            >
                              <User className="h-4 w-4 mr-2" />
                              Ver Cliente
                            </Button>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Cliente Detail Dialog */}
      <Dialog open={!!selectedCliente} onOpenChange={() => setSelectedCliente(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              {selectedCliente?.nombre}
            </DialogTitle>
          </DialogHeader>
          {selectedCliente && (
            <div className="space-y-4">
              <div className="grid gap-3 text-sm">
                {selectedCliente.empresa && (
                  <div className="flex items-center gap-3 p-3 rounded-md bg-accent/30">
                    <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <div>
                      <div className="text-xs text-muted-foreground">Empresa</div>
                      <div className="font-medium">{selectedCliente.empresa}</div>
                    </div>
                  </div>
                )}
                {selectedCliente.whatsapp && (
                  <div className="flex items-center gap-3 p-3 rounded-md bg-accent/30">
                    <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1">
                      <div className="text-xs text-muted-foreground">WhatsApp</div>
                      <div className="font-medium">{selectedCliente.whatsapp}</div>
                    </div>
                    <a
                      href={formatWhatsappLink(selectedCliente.whatsapp)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button size="sm" variant="outline" className="gap-1.5">
                        <MessageCircle className="h-3.5 w-3.5" />
                        Abrir
                      </Button>
                    </a>
                  </div>
                )}
                {selectedCliente.ciudad && (
                  <div className="flex items-center gap-3 p-3 rounded-md bg-accent/30">
                    <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <div>
                      <div className="text-xs text-muted-foreground">Ciudad</div>
                      <div className="font-medium">{selectedCliente.ciudad}</div>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-3 p-3 rounded-md bg-accent/30">
                  <User className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div>
                    <div className="text-xs text-muted-foreground">Estado</div>
                    <div className="font-medium">
                      <Badge variant={selectedCliente.estado === 'Activo' ? 'default' : 'destructive'}>
                        {selectedCliente.estado}
                      </Badge>
                      {selectedCliente.deletedAt && (
                        <span className="ml-2 text-xs text-muted-foreground">
                          (Eliminado el {new Date(selectedCliente.deletedAt).toLocaleDateString('es-UY')})
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {selectedCliente.rating != null && (
                  <div className="flex items-center gap-3 p-3 rounded-md bg-accent/30">
                    <span className="text-muted-foreground flex-shrink-0">⭐</span>
                    <div>
                      <div className="text-xs text-muted-foreground">Rating</div>
                      <div className="font-medium">{selectedCliente.rating}/5</div>
                    </div>
                  </div>
                )}
              </div>
              {selectedCliente.etiquetas && selectedCliente.etiquetas.length > 0 && (
                <div>
                  <div className="text-xs text-muted-foreground mb-2">Etiquetas</div>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedCliente.etiquetas.map((tag, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">{tag}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {selectedCliente.notas && (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Notas</div>
                  <p className="text-sm p-3 rounded-md bg-muted">{selectedCliente.notas}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Accept Offer Dialog replaced by Espera Dialog */}

      {/* Reject Offer Dialog */}
      <AlertDialog open={!!ofertaToReject} onOpenChange={() => setOfertaToReject(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Rechazar oferta?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción tipoGanadorá la oferta como rechazada.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleRejectOffer(ofertaToReject)}>
              Rechazar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Espera Dialog */}
      <Dialog open={showEsperaDialog} onOpenChange={(open) => {
        setShowEsperaDialog(open);
        if (!open) setOfertaToAccept(null);
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Pasar a "En espera"</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 mt-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">Fecha y hora estimada</label>
              <input
                type="datetime-local"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={fechaEspera}
                onChange={(e) => setFechaEspera(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Motivo / Comentario</label>
              <textarea
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Ej. Esperando respuesta del cliente X..."
                value={motivoEspera}
                onChange={(e) => setMotivoEspera(e.target.value)}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 text-sm text-muted-foreground pt-4 border-t">
            <Button variant="outline" onClick={() => setShowEsperaDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handlePasarAEspera}>
              Confirmar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* No Adjudicada Dialog */}
      <Dialog open={showNoAdjudicadoDialog} onOpenChange={setShowNoAdjudicadoDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>¿TipoGanador como No adjudicada?</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 mt-2">
            <p className="text-sm text-muted-foreground">
              Esta acción finalizará la publicación sin que haya sido adjudicada a ningún ofertante.
            </p>
            {ofertas.length > 0 && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Comentario / Motivo (Obligatorio)</label>
                <textarea
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="Explique el motivo por el cual no se adjudicó a pesar de tener ofertas..."
                  value={comentarioNoAdjudicada}
                  onChange={(e) => setComentarioNoAdjudicada(e.target.value)}
                />
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 text-sm text-muted-foreground pt-4 border-t">
            <Button variant="outline" onClick={() => setShowNoAdjudicadoDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleNoAdjudicado}>
              Confirmar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Adjudicar Dialog */}
      <Dialog open={showAdjudicarDialog} onOpenChange={setShowAdjudicarDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmar Adjudicación</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 mt-2">
            <p className="text-sm text-muted-foreground">
              ¿Está seguro de que desea tipoGanador este vehículo como adjudicado?
            </p>
            {ofertas.filter(o => o.estado === 'Aceptada').length > 0 ? (
              <div className="p-3 bg-green-50 border border-green-200 rounded-md dark:bg-green-900/20 dark:border-green-800">
                <div className="text-sm font-medium text-green-800 dark:text-green-400 mb-2">Oferta a adjudicar:</div>
                {ofertas.filter(o => o.estado === 'Aceptada').map(oferta => {
                  const cliente = clientes[oferta.clienteId];
                  return (
                    <div key={oferta.id} className="text-sm">
                      <span className="font-semibold text-green-900 dark:text-green-300">
                        ${oferta.monto.toLocaleString('en-US')} USD
                      </span>
                      <span className="text-muted-foreground ml-2">
                        por {cliente?.nombre || 'Cliente'}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md dark:bg-red-900/20 dark:border-red-800 text-sm text-red-800 dark:text-red-400">
                ⚠️ No hay ninguna oferta en estado "Aceptada". Debe aceptar una oferta antes de adjudicar.
              </div>
            )}
            <p className="text-sm text-muted-foreground mt-2">
              Esta acción notificará automáticamente al ganador y rechazará todas las ofertas pendientes restantes.
            </p>
          </div>
          <div className="flex justify-end gap-2 text-sm text-muted-foreground pt-4 border-t">
            <Button variant="outline" onClick={() => setShowAdjudicarDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleAdjudicar} 
              className="bg-green-600 hover:bg-green-700 text-white"
              disabled={ofertas.filter(o => o.estado === 'Aceptada').length === 0}
            >
              Confirmar Adjudicación
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reenviar Dialog */}
      <Dialog open={showReenviarDialog} onOpenChange={setShowReenviarDialog}>
        <DialogContent className="sm:max-w-md max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Reenviar Publicación</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-4 flex-1 overflow-hidden">
            <div className="flex justify-between items-center gap-2 flex-wrap">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleSelectBidders}
                className="text-xs"
              >
                Seleccionar Solo Con Oferta
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedClientesReenviar([])}
                className="text-xs"
              >
                Limpiar Selección
              </Button>
            </div>

            <div className="border rounded-md p-2 overflow-y-auto max-h-[45vh] space-y-1">
              {Object.values(clientes)
                .filter(isReenviarCandidate)
                .map((r) => {
                  const hasOffered = ofertas.some(o => o.clienteId === r.id);
                  return (
                    <div
                      key={r.id}
                      className="flex items-center justify-between p-2 hover:bg-accent/50 rounded-sm cursor-pointer border-b last:border-0 border-border/50"
                      onClick={() => toggleClienteSelection(r.id)}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={selectedClientesReenviar.includes(r.id)}
                          onChange={() => { }} // toggled by parent div
                          className="h-4 w-4 rounded border-gray-300 pointer-events-none"
                        />
                        <div>
                          <p className="text-sm font-medium leading-none">{r.nombre}</p>
                          {r.empresa && <p className="text-xs text-muted-foreground mt-1">{r.empresa}</p>}
                        </div>
                      </div>
                      {hasOffered && <Badge variant="secondary" className="text-[10px] bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20 hover:bg-amber-500/20">Ofertó</Badge>}
                    </div>
                  );
                })}
            </div>
            <div className="text-sm text-muted-foreground">
              {selectedClientesReenviar.length} seleccionados
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t mt-auto">
            <Button variant="outline" onClick={() => setShowReenviarDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleReenviar} disabled={reenviando || selectedClientesReenviar.length === 0}>
              {reenviando ? 'Enviando...' : 'Reenviar Ahora'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Extender Dialog */}
      <Dialog open={showExtenderDialog} onOpenChange={setShowExtenderDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Extender Plazo de Ofertas</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 mt-2">
            <p className="text-sm text-muted-foreground">
              Esta acción extenderá el tiempo disponible para recibir ofertas y notificará a todos los clientes que ya hayan ofertado.
            </p>
            {isVencida && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-md dark:bg-amber-900/20 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-400">
                ⚠️ La publicación ya está vencida. La extensión de tiempo se calculará a partir de este momento.
              </div>
            )}
            <div className="space-y-2">
              <label className="text-sm font-medium">Horas a extender</label>
              <Input
                type="number"
                min="1"
                value={horasExtender}
                onChange={(e) => setHorasExtender(e.target.value)}
                placeholder="Ej. 10"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 text-sm text-muted-foreground pt-4 border-t">
            <Button variant="outline" onClick={() => setShowExtenderDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleExtender} 
              disabled={extendiendo || !horasExtender || horasExtender < 1}
            >
              {extendiendo ? 'Extendiendo...' : 'Extender y Notificar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <NewManualOfferDialog
        open={showNewOfferDialog}
        onOpenChange={setShowNewOfferDialog}
        onSuccess={loadData}
        clientes={clientes}
        preselectedLote={lote}
      />

      {/* Edit TipoGanado/Categoria Dialog */}
      <Dialog open={showEditTipoGanadoCategoria} onOpenChange={setShowEditTipoGanadoCategoria}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar TipoGanado y Categoria</DialogTitle>
            <DialogDescription>
              Seleccione la tipoGanado y categoria correctos para esta publicación
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <TipoGanadoCategoriaSelector
              tipoGanado={editTipoGanado}
              categoria={editCategoria}
              onTipoGanadoChange={(val) => { setEditTipoGanado(val); if (val !== editTipoGanado) setEditCategoria(''); }}
              onCategoriaChange={(val) => setEditCategoria(val)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditTipoGanadoCategoria(false)} disabled={savingTipoGanadoCategoria}>
              Cancelar
            </Button>
            <Button
              onClick={async () => {
                if (!editTipoGanado || !editCategoria) {
                  toast.error('TipoGanado y categoria son requeridos');
                  return;
                }
                setSavingTipoGanadoCategoria(true);
                try {
                  await axios.put(`${API}/lotes/${id}`, { tipoGanado: editTipoGanado, categoria: editCategoria });
                  toast.success('TipoGanado y categoria actualizados');
                  setShowEditTipoGanadoCategoria(false);
                  loadData();
                } catch (error) {
                  console.error('Error updating tipoGanado/categoria:', error);
                  toast.error('Error al actualizar tipoGanado y categoria');
                } finally {
                  setSavingTipoGanadoCategoria(false);
                }
              }}
              disabled={savingTipoGanadoCategoria}
              data-testid="save-edit-tipoGanado-categoria"
            >
              {savingTipoGanadoCategoria ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DetallePublicacion;